import { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { notificationsService } from '../services/notificationsService';
import { useNotifications } from '../hooks/useNotifications';

/**
 * NotificationCenter - Sistema de notificações isolado
 * Funciona independente do resto do app, nunca quebra a UI
 */
export default function NotificationCenter() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Carregar contador inicial
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // Conectar ao SignalR
  const handleNewNotification = useCallback((notification) => {
    console.log('[NotificationCenter] New notification received:', notification);
    
    // Adicionar nova notificação no topo
    setNotifications((prev) => [notification, ...prev]);
    
    // Incrementar contador
    setUnreadCount((prev) => prev + 1);
    
    // Mostrar notificação do navegador (opcional)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Nova Notificação', {
        body: notification.message || 'Você tem uma nova notificação',
        icon: '/favicon.ico',
      });
    }
  }, []);

  const { isConnected } = useNotifications(handleNewNotification);

  const loadUnreadCount = async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
      setHasError(false);
    } catch (error) {
      console.warn('[NotificationCenter] Error loading unread count');
      setHasError(true);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsService.getNotifications(0, 20);
      setNotifications(data);
      setHasError(false);
    } catch (error) {
      console.warn('[NotificationCenter] Error loading notifications');
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();
    
    try {
      const success = await notificationsService.markAsRead(notificationId);
      
      if (success) {
        // Atualizar localmente
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        
        // Atualizar contador
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.warn('[NotificationCenter] Error marking as read');
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'agora';
      if (diffMins < 60) return `${diffMins}min atrás`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h atrás`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d atrás`;
      
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  const open = Boolean(anchorEl);

  // Se houver erro, mostrar ícone sem badge (silencioso)
  if (hasError) {
    return (
      <Tooltip title="Notificações temporariamente indisponíveis">
        <IconButton color="inherit" disabled>
          <NotificationsNoneIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={isConnected ? 'Notificações (conectado)' : 'Notificações'}>
        <IconButton color="inherit" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error">
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            Notificações
          </Typography>
          {isConnected && (
            <Chip
              label="Ao vivo"
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: 'success.main',
                color: 'white',
                height: 20,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              Nenhuma notificação
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 500, overflow: 'auto' }}>
            {notifications.map((notification, index) => {
              const isUnread = !notification.readAt;
              
              return (
                <Box key={notification.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      isUnread && (
                        <Tooltip title="Marcar como lida">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <ListItemButton
                      sx={{
                        py: 2,
                        bgcolor: isUnread ? 'action.hover' : 'transparent',
                      }}
                    >
                      {isUnread && (
                        <CircleIcon
                          sx={{
                            fontSize: 8,
                            color: 'primary.main',
                            mr: 1,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={isUnread ? 'bold' : 'normal'}
                            color={isUnread ? 'text.primary' : 'text.secondary'}
                          >
                            {notification.title || 'Notificação'}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color={isUnread ? 'text.secondary' : 'text.disabled'}
                              sx={{ mt: 0.5 }}
                            >
                              {notification.message || 'Você tem uma nova notificação'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              {formatDate(notification.createdAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={handleClose}>
                Fechar
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
